import { getIn as get, setIn as set } from 'final-form';

/** @typedef {{ intl: import('react-intl').IntlShape, [field: string]: unknown }} ValidationMeta */
/** @typedef {(value: any, meta: any) => Record<string, any> | string | false | undefined} ValidatorFn */
/** @typedef {Record<string, import('modules/global/typing/common/common').MayBe<ValidatorFn>>} ConfigObject */
/** @typedef {Array<ValidatorFn>} ConfigArray */
/** @typedef {ConfigObject | ConfigArray} Config */

/**
 * @param {string | Object} key
 * @param {import('react-intl').IntlShape} intl
 * @returns {string | Object}
 */
const getError = (key, intl, intlValues) => {
  if (typeof key === 'string') {
    return intl.formatMessage({ id: key, defaultMessage: key }, intlValues);
  }
  if (typeof key === 'object') {
    return Object.keys(key).length > 0 ? key : undefined;
  }
  return key;
};

/**
 * Universal validation function that validates entities (objects or arrays) using
 * configurable validation rules. This is the core validation utility used throughout
 * the application for form validation, data validation, and nested object validation.
 *
 * Supports two main validation patterns:
 * - **Object validation**: Uses a config object where keys are property paths and values are validators
 * - **Array validation**: Uses a config array where each element validates the corresponding array item
 *
 * Key features:
 * - Path-based validation using dot notation (e.g., 'nested.field')
 * - Conditional validation (validators can return undefined to skip)
 * - Internationalization support via meta.intl
 * - Error aggregation and formatting
 * - Composable validators for complex nested structures
 * - Integration with final-form for path resolution
 *
 * @template M - The type of the meta validation context
 *
 * @param {Config} config - Validation configuration:
 *   - For objects: `Record<string, (value, meta) => string | object | undefined>`
 *   - For arrays: `Array<(item, meta) => string | object | undefined>`
 * @param {object | any[] | undefined | null} entity - The entity to validate (object or array)
 * @param {ValidationMeta & M} meta - Validation context containing intl formatter and other metadata
 *
 * @returns {Record<string, string | any> | undefined} Validation errors object:
 *   - For objects: Returns object with paths as keys and error messages as values
 *   - For arrays: Returns array with errors at corresponding indices
 *   - Returns `undefined` if no validation errors found
 *
 * @example
 * // Object validation with path-based validators
 * const userValidator = validateEntity({
 *   'name': (value) => !value ? 'validation.required' : undefined,
 *   'email': (value) => !value?.includes('@') ? 'validation.email' : undefined,
 *   'profile.age': (value) => value < 18 ? 'validation.minAge' : undefined
 * }, userData, { intl, defaultLanguage: 'en' });
 * // Returns: { name: 'Name is required', email: 'Invalid email' }
 *
 * @example
 * // Array validation with item validators
 * const itemsValidator = validateEntity(
 *   items?.map(() => itemValidator),
 *   itemsArray,
 *   meta
 * );
 * // Returns: [undefined, { field: 'error' }, undefined] for array with error on second item
 *
 * @example
 * // Conditional validation with context
 * const conditionalValidator = validateEntity({
 *   'documents': (docs, meta) => meta.enforceDocuments ? validateDocuments(docs, meta) : undefined,
 *   'signature': (sig) => sig?.status === 'REQUIRED' ? validateSignature(sig) : undefined
 * }, entity, meta);
 *
 * @example
 * // Nested validation composition
 * const orderValidator = validateEntity({
 *   'customer': (customer, meta) => validateEntity(customerConfig, customer, meta),
 *   'items': (items, meta) => validateEntity(items?.map(() => itemValidator), items, meta)
 * }, order, meta);
 */
export const validateEntity = (config, entity, meta) => {
  if (Array.isArray(config)) {
    const errors = config
      .map((validateFunc, index) =>
        getError(validateFunc?.(entity[index], meta), meta.intl, meta.intlValues));
    return errors.some(Boolean) ? errors : undefined;
  }
  return Object.entries(config ?? {}).reduce((errors, [path, validateFunc]) => {
    const value = get(entity, path);
    const error = getError(validateFunc?.(value, meta), meta.intl, meta.intlValues);
    return error ? set(errors, path, error) : errors;
  }, {});
};
